import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { STUDENT_DISCOUNT_FAQ_KEYS } from '../../../utils/studentDiscount'

type FaqItem = { id: string; q: string; a: string; action?: { label: string; onClick: () => void } }

const faq = (t: TFunction, trialDays: number | null, onAskDiscount: () => void): FaqItem[] => [
  { id: 'whichPlan', q: t('faq.whichPlan.q'), a: t('faq.whichPlan.a') },
  { id: 'whoWrites', q: t('faq.whoWrites.q'), a: t('faq.whoWrites.a') },
  // Told before purchase: corrections and calls happen in Chinese or English, in every market.
  { id: 'teacherLanguage', q: t('faq.teacherLanguage.q'), a: t('common:teacherLanguageNotice') },
  ...(trialDays ? [{ id: 'trial', q: t('faq.trial.q'), a: t('faq.trial.a', { days: trialDays }) }] : []),
  {
    id: 'studentDiscount',
    q: t(STUDENT_DISCOUNT_FAQ_KEYS.q),
    a: t(STUDENT_DISCOUNT_FAQ_KEYS.a),
    action: { label: t(STUDENT_DISCOUNT_FAQ_KEYS.action), onClick: onAskDiscount },
  },
  // Web plan switches go through Stripe with proration_behavior 'create_prorations' (profile.service
  // createStripeCheckOutSession), so unused time on the old plan is credited against the new one.
  // App Store / Google Play payers aren't billed by Stripe: buying here would start a second
  // subscription while the store keeps charging, so they are sent to support first.
  { id: 'changePlan', q: t('faq.changePlan.q'), a: t('faq.changePlan.a') },
  { id: 'missDay', q: t('faq.missDay.q'), a: t('faq.missDay.a') },
]

/** Single-open accordion; collapsed answers are unmounted, so in-page search skips them. */
const PlanFaq = ({ trialDays, onAskDiscount }: { trialDays: number | null; onAskDiscount: () => void }) => {
  // The student discount entry is shared with onboarding, so its strings live in `offers`.
  const { t } = useTranslation(['plan', 'offers'])
  const [open, setOpen] = useState<number | null>(null)
  return (
    <section className='plan-faq' aria-labelledby='plan-faq-title'>
      <h2 className='plan-section-title' id='plan-faq-title'>
        {t('faq.title')}
      </h2>
      <ul className='plan-faq-list'>
        {faq(t, trialDays, onAskDiscount).map((item, i) => {
          const isOpen = open === i
          return (
            <li className={`plan-faq-item${isOpen ? ' is-open' : ''}`} key={item.id}>
              <button
                type='button'
                className='plan-faq-q'
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : i)}
              >
                <span>{item.q}</span>
                <span className='plan-faq-chev' aria-hidden='true' />
              </button>
              {isOpen && (
                <div className='plan-faq-a'>
                  <p>{item.a}</p>
                  {item.action && (
                    <button type='button' className='plan-faq-action' onClick={item.action.onClick}>
                      {item.action.label}
                    </button>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default PlanFaq
